import {
  booleanPointInPolygon,
  distance,
  nearestPointOnLine,
  point,
  polygonToLine,
} from '@turf/turf';

import { FieldFeature } from '@fields/data-access';

export const HARVEST_PROOF_MAX_DISTANCE_METERS = 50;

function distanceToPolygonBoundaryMeters(
  userPoint: GeoJSON.Feature<GeoJSON.Point>,
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon,
): number | null {
  const outline = polygonToLine(geometry);
  const lineFeatures =
    outline.type === 'FeatureCollection' ? outline.features : [outline];

  let minDistance = Infinity;

  for (const lineFeature of lineFeatures) {
    if (lineFeature.geometry?.type !== 'LineString') {
      continue;
    }

    const nearest = nearestPointOnLine(
      lineFeature as GeoJSON.Feature<GeoJSON.LineString>,
      userPoint,
    );
    const segmentDistance = distance(userPoint, nearest, { units: 'meters' });
    if (segmentDistance < minDistance) {
      minDistance = segmentDistance;
    }
  }

  return minDistance === Infinity ? null : minDistance;
}

export function getDistanceToFieldMeters(
  field: FieldFeature,
  latitude: number,
  longitude: number,
): number | null {
  if (!field?.geometry) {
    return null;
  }

  const userPoint = point([longitude, latitude]);
  const geometry = field.geometry;

  if (
    booleanPointInPolygon(
      userPoint,
      geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon,
    )
  ) {
    return 0;
  }

  return distanceToPolygonBoundaryMeters(
    userPoint,
    geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon,
  );
}

export function isCloseEnoughToField(
  field: FieldFeature,
  latitude: number | null | undefined,
  longitude: number | null | undefined,
  maxDistance = HARVEST_PROOF_MAX_DISTANCE_METERS,
): boolean {
  if (latitude == null || longitude == null) {
    return false;
  }

  const meters = getDistanceToFieldMeters(field, latitude, longitude);
  return meters !== null && meters <= maxDistance;
}
